import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect } from "react";

import { api, call } from "../api/client";
import { useMe } from "../lib/auth";
import { FOOD_INDEX_KEY, fetchFoodIndex } from "../lib/foods";
import { isoDate } from "../lib/format";
import { useToday } from "../lib/today";
import { startSync, stopSync } from "./engine";

// Days fetched ahead of time, so they can be opened offline.
const PREFETCH_DAYS = 14;
const PREFETCH_STALE_MS = 5 * 60_000;

/** Runs while signed in: sends pending changes and keeps recent data saved on the device. */
export function SyncRunner() {
  const me = useMe();
  const today = useToday();
  const queryClient = useQueryClient();
  const userId = me.data?.id;

  useEffect(() => {
    if (userId === undefined) return;
    startSync(userId, queryClient);
    return stopSync;
  }, [userId, queryClient]);

  useEffect(() => {
    if (userId === undefined) return;
    const prefetch = <T,>(queryKey: readonly unknown[], queryFn: () => Promise<T>) =>
      queryClient.prefetchQuery({ queryKey, queryFn, staleTime: PREFETCH_STALE_MS }).catch(() => undefined);
    for (let i = 0; i < PREFETCH_DAYS; i++) {
      const day = isoDate(dayjs(today).subtract(i, "day"));
      void prefetch(["summary", "day", day], () =>
        call(api.GET("/api/v1/summary/day/{day}", { params: { path: { day } } })),
      );
    }
    void prefetch(FOOD_INDEX_KEY, fetchFoodIndex);
    void prefetch(["goals"], () => call(api.GET("/api/v1/goals")));
  }, [userId, today, queryClient]);

  return null;
}
